/* @flow */

import React, { Component } from 'react';
import { connect } from 'react-redux';
import { compose, withProps } from 'recompose';
import { get, map, memoize } from 'lodash/fp';
import { Div } from 'glamorous';
import { getCountryStates } from 'app/api/countries';
import withAPICollection from 'app/hoc/withAPICollection';
import { normalizeAddress } from 'app/utils/geocodeUtils';
import GeoAddressField from 'app/components/form/GeoAddressField';
import Grid from 'app/components/layout/Grid';
import GridCol from 'app/components/layout/GridCol';
import HideAndShow from 'app/components/commons/HideAndShow';
import InputField from 'app/components/commons/InputField';
import StateSelect from 'app/components/form/StateSelect';
import { displayAddress } from 'app/utils/displayUtils';
import t from 'app/utils/translate';

type Props = {
  city?: string,
  countryCode?: string,
  countryId?: number,
  hideAddressSuggest?: boolean,
  states?: Array<*>,
  googleMapsAPIKey: string,
  lightContent?: boolean,
  onChange: Object => void,
  postcode?: string,
  streetAddress?: string,
  state?: string,
  onFetchStates: () => void,
};

type State = {
  showAddressFields: boolean,
};

class AddressField extends Component<Props, State> {
  props: Props;

  state: State = {
    showAddressFields: false,
  };

  static defaultProps = {
    city: undefined,
    countryCode: undefined,
    countryId: undefined,
    hideAddressSuggest: false,
    lightContent: false,
    postcode: undefined,
    streetAddress: undefined,
    state: undefined,
    states: undefined,
  };

  componentDidMount() {
    const { countryId, onFetchStates, hideAddressSuggest } = this.props;

    if (hideAddressSuggest && countryId) {
      onFetchStates(countryId);
    }
  }

  handleGeoAddressChange = (data) => {
    const {
      city,
      postcode,
      state,
      streetAddress,
    } = normalizeAddress(data);

    const { onChange } = this.props;

    onChange({
      streetAddress,
      city,
      postcode,
      state,
    });
  };

  handleFieldChange = memoize((key: string) => (value: string) => {
    const { onChange } = this.props;

    onChange({
      [key]: value,
    });
  });

  toggleAddressFields = () => {
    this.setState(state => ({
      showAddressFields: !state.showAddressFields,
    }));
  };

  renderAddressGrid = () => {
    const {
      city,
      states,
      lightContent,
      postcode,
      state,
      streetAddress,
    } = this.props;

    return (
      <Grid>
        <GridCol width="one-half">
          <InputField
            label={t('Street Address')}
            lightContent={lightContent}
            name="streetAddress"
            onChange={this.handleFieldChange('streetAddress')}
            value={streetAddress}
            type="text"
          />
        </GridCol>
        <GridCol width="one-half">
          <InputField
            label={t('City')}
            lightContent={lightContent}
            name="city"
            onChange={this.handleFieldChange('city')}
            value={city}
            type="text"
          />
        </GridCol>
        <GridCol width="one-half">
          {Array.isArray(states) && states.length ? (
            <StateSelect
              label="State"
              options={states}
              value={state}
              lightContent={lightContent}
              onChange={this.handleFieldChange('state')}
            />
          ) : (
            <InputField
              label={t('State')}
              lightContent={lightContent}
              name="state"
              onChange={this.handleFieldChange('state')}
              value={state}
              type="text"
            />
          )}
        </GridCol>
        <GridCol width="one-half">
          <InputField
            label={t('Postcode')}
            lightContent={lightContent}
            name="postcode"
            onChange={this.handleFieldChange('postcode')}
            value={postcode}
            type="text"
          />
        </GridCol>
      </Grid>
    );
  }

  render() {
    const {
      city,
      countryCode,
      googleMapsAPIKey,
      lightContent,
      postcode,
      hideAddressSuggest,
      state,
      streetAddress,
    } = this.props;

    const {
      showAddressFields,
    } = this.state;

    const addressString = displayAddress({
      streetAddress,
      city,
      state,
      postcode,
    });

    return (
      <Div flexBasis="100%">
        {hideAddressSuggest ? this.renderAddressGrid() : (
          <React.Fragment>
            <GeoAddressField
              apiKey={googleMapsAPIKey}
              country={countryCode}
              disabled={!countryCode || showAddressFields}
              initialValue={addressString}
              label={addressString ? 'Address' : 'Lookup Address'}
              lightContent={lightContent}
              name="full_address"
              onChange={this.handleGeoAddressChange}
            />
            <HideAndShow
              hideText="Hide address fields"
              onClick={this.toggleAddressFields}
              show={showAddressFields}
              showText="Address not found?"
            >
              {this.renderAddressGrid()}
            </HideAndShow>
          </React.Fragment>
        )}
      </Div>
    );
  }
}

const apiWrapper = compose(
  withAPICollection({
    fetcher: getCountryStates,
    normalizeData: map(({ active, code, id, name }) => ({
      id,
      label: name,
      value: code,
      disabled: active !== 1,
    })),
  }),
  withProps(({ items, onFetch }) => ({
    states: items,
    onFetchStates: countryId => onFetch(countryId),
  })),
);

const connector = connect(
  ({ settings }) => ({
    hideAddressSuggest: get('hideAddressSuggest', settings),
    googleMapsAPIKey: settings.googleMapsAPIKey,
  }),
);

export default compose(apiWrapper, connector)(AddressField);
